import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  Loader2,
  Hash,
  Trash2,
  Users,
  Lock,
  Save,
  CheckSquare,
  Pencil,
  Clock,
  Calendar,
} from 'lucide-react';
import { FormModal, MODAL_COLORS, useModal } from '@/components/modal';
import { useRoleDetail, useDeleteRole, useUpdateRolePermissions, useUpdateRole, useAvailablePermissions } from '../hooks';
import { triggerHapticFeedback } from '../utils/adminAnimations';
import { localizedName } from '@/utils/localized';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import toast from 'react-hot-toast';
import type { Role } from '../types';

const cardStyle = {
  backgroundColor: MODAL_COLORS.interactive,
  border: `1px solid ${MODAL_COLORS.border}`,
};

const actionBtnStyle = {
  border: `1px solid ${MODAL_COLORS.border}`,
};

interface RoleDetailModalProps {
  role: Role;
  onClose: () => void;
}

export function RoleDetailModal({ role, onClose }: RoleDetailModalProps) {
  const { t, i18n } = useTranslation();
  const { openConfirm } = useModal();
  const { isSuperAdmin } = usePermissions();

  const deleteMutation = useDeleteRole();
  const permissionsMutation = useUpdateRolePermissions();
  const updateRoleMutation = useUpdateRole();
  const { data: roleDetail } = useRoleDetail(role.id);
  const { data: availablePermissions } = useAvailablePermissions();

  const tabPermissions = availablePermissions?.tab ?? [];
  const actionPermissions = availablePermissions?.action ?? [];
  const dataScopePermissions = availablePermissions?.dataScope ?? [];

  const [isOpen, setIsOpen] = useState(true);
  const requestClose = useCallback(() => setIsOpen(false), []);

  // Permission state
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [permissionsChanged, setPermissionsChanged] = useState(false);

  // Editable metadata state
  const [editName, setEditName] = useState(role.name);
  const [editNameEn, setEditNameEn] = useState(role.nameEn || '');
  const [editDescription, setEditDescription] = useState(role.description || '');
  const [editColor, setEditColor] = useState(role.color || '#1565c0');
  const [editPaymentCycle, setEditPaymentCycle] = useState(role.paymentCycle || '');
  const [metadataChanged, setMetadataChanged] = useState(false);

  // Sync from roleDetail when loaded
  useEffect(() => {
    if (roleDetail) {
      setEditName(roleDetail.name);
      setEditNameEn(roleDetail.nameEn || '');
      setEditDescription(roleDetail.description || '');
      setEditColor(roleDetail.color || '#1565c0');
      setEditPaymentCycle((roleDetail as any).paymentCycle || '');
      setMetadataChanged(false);
    }
  }, [roleDetail]);

  // Sync permissions from server
  useEffect(() => {
    if (roleDetail?.permissions) {
      setSelectedPermissions(new Set(roleDetail.permissions.map((p) => p.permission)));
      setPermissionsChanged(false);
    }
  }, [roleDetail]);

  const isSuperAdminRole = !!role.isSystem;
  const canEdit = isSuperAdmin() && !isSuperAdminRole;
  const canEditPermissions = canEdit;

  const handleMetadataChange = () => setMetadataChanged(true);

  const togglePermission = (permission: string) => {
    if (!canEditPermissions) return;
    const newSet = new Set(selectedPermissions);
    if (newSet.has(permission)) newSet.delete(permission);
    else newSet.add(permission);
    setSelectedPermissions(newSet);
    setPermissionsChanged(true);
    triggerHapticFeedback();
  };

  const hasChangesAll = metadataChanged || permissionsChanged;

  const handleSave = async () => {
    try {
      triggerHapticFeedback();
      const promises: Promise<unknown>[] = [];

      if (metadataChanged) {
        promises.push(
          updateRoleMutation.mutateAsync({
            id: role.id,
            data: {
              name: editName,
              nameEn: editNameEn || undefined,
              description: editDescription || undefined,
              color: editColor || undefined,
              paymentCycle: editPaymentCycle || undefined,
            },
          })
        );
      }

      if (permissionsChanged) {
        promises.push(
          permissionsMutation.mutateAsync({
            id: role.id,
            permissions: Array.from(selectedPermissions),
          })
        );
      }

      await Promise.all(promises);
      toast.success(t('rolesAdmin.updateSuccess'));
      setMetadataChanged(false);
      setPermissionsChanged(false);
    } catch {
      toast.error(t('rolesAdmin.updateFailed'));
    }
  };

  const isSaving = updateRoleMutation.isPending || permissionsMutation.isPending;

  const renderPermissionSection = (
    title: string,
    permissions: readonly string[]
  ) => (
    <div className="space-y-1.5">
      <h4 className="text-xs font-bold opacity-70">{title}</h4>
      <div className="grid grid-cols-1 gap-1">
        {permissions.map((perm) => {
          const isChecked = isSuperAdminRole || selectedPermissions.has(perm);
          const isDisabled = !canEditPermissions;

          return (
            <label
              key={perm}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg transition-colors text-sm ${
                isDisabled ? 'cursor-default' : 'cursor-pointer hover:bg-white/50'
              }`}
              style={{
                backgroundColor: isChecked ? 'rgba(232, 245, 233, 0.6)' : MODAL_COLORS.surface,
                border: `1px solid ${isChecked ? 'rgba(4, 120, 87, 0.3)' : MODAL_COLORS.border}`,
              }}
            >
              <input
                type="checkbox"
                checked={isChecked}
                disabled={isDisabled}
                onChange={() => togglePermission(perm)}
                className="w-3.5 h-3.5 accent-green-600"
              />
              <span className={`text-xs ${isChecked ? 'font-semibold' : 'opacity-70'}`}>
                {t(`rolesAdmin.permissionLabels.${perm}`, perm)}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );

  return (
    <FormModal
      isOpen={isOpen}
      onClose={requestClose}
      onExitComplete={onClose}
      title={localizedName(role, i18n.language)}
      maxWidth="max-w-lg"
    >
      <div className="space-y-2">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl p-2 text-center" style={cardStyle}>
            <CheckSquare className="w-5 h-5 mx-auto mb-1 opacity-60" />
            <p className="text-xl font-bold">{roleDetail?.permissionCount ?? role.permissionCount ?? 0}</p>
            <p className="text-xs font-semibold opacity-60">{t('rolesAdmin.permissionCount')}</p>
          </div>
          <div className="rounded-xl p-2 text-center" style={cardStyle}>
            <Users className="w-5 h-5 mx-auto mb-1 opacity-60" />
            <p className="text-xl font-bold">{roleDetail?.userCount ?? role.userCount ?? 0}</p>
            <p className="text-xs font-semibold opacity-60">{t('rolesAdmin.userCount')}</p>
          </div>
        </div>

        {/* System badge */}
        {role.isSystem && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={cardStyle}>
            <Lock className="w-4 h-4 text-orange-600" />
            <span className="text-sm font-bold text-orange-800">{t('rolesAdmin.system')}</span>
          </div>
        )}

        {/* Permissions Matrix */}
        <div className="rounded-xl p-3 space-y-3" style={cardStyle}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 opacity-60" />
              <span className="text-sm font-bold">{t('rolesAdmin.permissionsMatrix')}</span>
            </div>
            {!canEditPermissions && (
              <span className="text-xs opacity-50 font-semibold">
                {isSuperAdminRole ? t('rolesAdmin.allPermissions') : t('rolesAdmin.viewOnly')}
              </span>
            )}
          </div>

          {renderPermissionSection(t('rolesAdmin.tabAccess'), tabPermissions)}
          {renderPermissionSection(t('rolesAdmin.actions'), actionPermissions)}
          {renderPermissionSection(t('rolesAdmin.dataScope'), dataScopePermissions)}
        </div>

        {/* Editable Details */}
        <div className="rounded-xl p-3 space-y-2" style={cardStyle}>
          <div className="flex items-center gap-2 mb-1">
            <Pencil className="w-4 h-4 opacity-60" />
            <span className="text-sm font-bold">{t('rolesAdmin.details', 'Details')}</span>
          </div>

          {/* Code — always read-only */}
          <div className="flex items-center gap-2 text-sm">
            <Hash className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('rolesAdmin.code')}:</span>
            <span className="opacity-70 font-mono">{role.code}</span>
          </div>

          {/* Name */}
          <div>
            <label className="block text-xs font-bold mb-0.5 opacity-70">{t('rolesAdmin.name')}</label>
            <input
              type="text"
              value={editName}
              onChange={(e) => { setEditName(e.target.value); handleMetadataChange(); }}
              disabled={!canEdit}
              className="w-full rounded-lg px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 transition-all disabled:opacity-60"
              style={{ backgroundColor: '#ffffff', border: `1px solid ${MODAL_COLORS.border}` }}
            />
          </div>

          {/* Name (English) */}
          <div>
            <label className="block text-xs font-bold mb-0.5 opacity-70">{t('rolesAdmin.nameEn')}</label>
            <input
              type="text"
              value={editNameEn}
              onChange={(e) => { setEditNameEn(e.target.value); handleMetadataChange(); }}
              disabled={!canEdit}
              dir="ltr"
              className="w-full rounded-lg px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 transition-all disabled:opacity-60"
              style={{ backgroundColor: '#ffffff', border: `1px solid ${MODAL_COLORS.border}` }}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-bold mb-0.5 opacity-70">{t('rolesAdmin.description')}</label>
            <textarea
              value={editDescription}
              onChange={(e) => { setEditDescription(e.target.value); handleMetadataChange(); }}
              disabled={!canEdit}
              rows={2}
              className="w-full rounded-lg px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 transition-all resize-none disabled:opacity-60"
              style={{ backgroundColor: '#ffffff', border: `1px solid ${MODAL_COLORS.border}` }}
            />
          </div>

          {/* Color */}
          <div>
            <label className="block text-xs font-bold mb-0.5 opacity-70">{t('rolesAdmin.color')}</label>
            <div className="flex items-center gap-1.5">
              <input
                type="color"
                value={editColor}
                onChange={(e) => { setEditColor(e.target.value); handleMetadataChange(); }}
                disabled={!canEdit}
                className="w-8 h-8 rounded-lg cursor-pointer border-0 p-0 disabled:opacity-60"
              />
              <input
                type="text"
                value={editColor}
                onChange={(e) => { setEditColor(e.target.value); handleMetadataChange(); }}
                disabled={!canEdit}
                dir="ltr"
                className="flex-1 rounded-lg px-2 py-1.5 text-xs font-mono outline-none focus:ring-2 focus:ring-primary-500 transition-all disabled:opacity-60"
                style={{ backgroundColor: '#ffffff', border: `1px solid ${MODAL_COLORS.border}` }}
              />
            </div>
          </div>

          {/* Payment Cycle */}
          <div>
            <label className="block text-xs font-bold mb-0.5 opacity-70">
              <Clock className="w-3 h-3 inline-block me-1" />
              {t('rolesAdmin.paymentCycle', 'Payment Cycle')}
            </label>
            <select
              value={editPaymentCycle}
              onChange={(e) => { setEditPaymentCycle(e.target.value); handleMetadataChange(); }}
              disabled={!canEdit}
              className="w-full rounded-lg px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 transition-all disabled:opacity-60 appearance-none cursor-pointer"
              style={{ backgroundColor: '#ffffff', border: `1px solid ${MODAL_COLORS.border}` }}
            >
              <option value="">{t('rolesAdmin.paymentCycleNone', 'Not set')}</option>
              <option value="FIXED_MONTHLY">{t('rolesAdmin.paymentCycleFixed', 'Fixed Monthly')}</option>
              <option value="DAILY">{t('rolesAdmin.paymentCycleDaily', 'Daily')}</option>
              <option value="WEEKLY">{t('rolesAdmin.paymentCycleWeekly', 'Weekly')}</option>
              <option value="BIWEEKLY">{t('rolesAdmin.paymentCycleBiweekly', 'Bi-Weekly')}</option>
            </select>
          </div>
        </div>

        {/* Save Button */}
        {canEdit && hasChangesAll && (
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold hover:translate-x-[1px] hover:translate-y-[1px] transition-transform cursor-pointer disabled:opacity-50 text-white"
            style={{ backgroundColor: 'var(--color-primary-500)', ...actionBtnStyle }}
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {t('rolesAdmin.saveChanges', 'Save Changes')}
          </button>
        )}

        {/* Dates */}
        <div className="rounded-xl p-3 space-y-1" style={cardStyle}>
          <div className="flex items-center gap-2 text-xs">
            <Calendar className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('rolesAdmin.createdAt')}:</span>
            <span className="opacity-70">{new Date(role.createdAt).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Clock className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('rolesAdmin.updatedAt')}:</span>
            <span className="opacity-70">{new Date(role.updatedAt).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Delete Section */}
        {isSuperAdmin() && !role.isSystem && (
          <button
            onClick={() => {
              triggerHapticFeedback();
              openConfirm({
                title: t('rolesAdmin.delete'),
                message: t('rolesAdmin.deleteConfirm'),
                variant: 'danger',
                confirmLabel: t('common.delete'),
                onConfirm: async () => {
                  try {
                    await deleteMutation.mutateAsync(role.id);
                    toast.success(t('rolesAdmin.deleteSuccess'));
                    requestClose();
                  } catch (err: any) {
                    const msg = err.response?.data?.message || '';
                    if (msg.includes('system')) {
                      toast.error(t('rolesAdmin.cannotDeleteSystem'));
                    } else if (msg.includes('user')) {
                      toast.error(t('rolesAdmin.cannotDeleteWithUsers'));
                    } else {
                      toast.error(t('rolesAdmin.deleteFailed'));
                    }
                  }
                },
              });
            }}
            disabled={deleteMutation.isPending}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold hover:translate-x-[1px] hover:translate-y-[1px] transition-transform cursor-pointer disabled:opacity-50"
            style={{
              backgroundColor: 'rgba(254, 226, 226, 0.6)',
              border: `1px solid ${MODAL_COLORS.border}`,
              color: '#B91C1C',
            }}
          >
            <Trash2 className="w-4 h-4" />
            {t('rolesAdmin.delete')}
          </button>
        )}
      </div>
    </FormModal>
  );
}
